import { isResearchRecord } from "./collection.js";
import { createUuid, deepClone, nowIso, shortId } from "./utils.js";

// A versão 3 usa uma base local isolada, mas importa registros ainda não enviados da v2.
const DB_NAME = "pdapp-offline-v3";
const LEGACY_DB_NAME = "pdapp-offline-v2";
const DB_VERSION = 1;
const SETTINGS_KEY = "app";

export const INTERVIEW_STATES = Object.freeze([
  "draft",
  "abandoned",
  "screened_out",
  "pending",
  "syncing",
  "synced",
  "error"
]);

let databasePromise;

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error("Transação cancelada."));
  });
}

export function openDatabase() {
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Feche outras abas do aplicativo para atualizar o banco local."));
    request.onupgradeneeded = event => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains("interviews")) {
        const interviews = database.createObjectStore("interviews", { keyPath: "id" });
        interviews.createIndex("updatedAt", "updatedAt");
        interviews.createIndex("syncStatus", "syncStatus");
      }

      if (!database.objectStoreNames.contains("syncQueue")) {
        const queue = database.createObjectStore("syncQueue", { keyPath: "id" });
        queue.createIndex("status", "status");
        queue.createIndex("createdAt", "createdAt");
        queue.createIndex("interviewId", "interviewId");
      }

      if (!database.objectStoreNames.contains("settings")) {
        database.createObjectStore("settings", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });

  return databasePromise;
}

async function legacyDatabaseExists() {
  if (typeof indexedDB.databases !== "function") return false;
  const databases = await indexedDB.databases();
  return databases.some(item => item.name === LEGACY_DB_NAME);
}

function openLegacyDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LEGACY_DB_NAME);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

function readLegacyStore(database, storeName) {
  if (!database.objectStoreNames.contains(storeName)) return Promise.resolve([]);
  const transaction = database.transaction(storeName, "readonly");
  return requestPromise(transaction.objectStore(storeName).getAll());
}

export async function migrateLegacyDatabase() {
  if (!(await legacyDatabaseExists())) return { migrated: false, interviews: 0, queue: 0 };

  const legacy = await openLegacyDatabase();
  try {
    const [interviews, queue, settings] = await Promise.all([
      readLegacyStore(legacy, "interviews"),
      readLegacyStore(legacy, "syncQueue"),
      readLegacyStore(legacy, "settings")
    ]);
    const target = await openDatabase();
    const transaction = target.transaction(["interviews", "syncQueue", "settings"], "readwrite");
    const interviewStore = transaction.objectStore("interviews");
    const queueStore = transaction.objectStore("syncQueue");
    const settingsStore = transaction.objectStore("settings");

    for (const interview of interviews) {
      interviewStore.put(deepClone({
        ...interview,
        respondentId: interview.respondentId || interview.id
      }));
    }
    for (const item of queue) {
      queueStore.put(deepClone({
        ...item,
        status: item.status === "syncing" ? "pending" : item.status,
        payload: item.payload ? {
          ...item.payload,
          respondentId: item.payload.respondentId || item.payload.id
        } : item.payload
      }));
    }
    for (const setting of settings) settingsStore.put(deepClone(setting));
    await transactionPromise(transaction);
    return { migrated: interviews.length > 0 || queue.length > 0, interviews: interviews.length, queue: queue.length };
  } finally {
    legacy.close();
  }
}

async function getAll(storeName) {
  const database = await openDatabase();
  const transaction = database.transaction(storeName, "readonly");
  return requestPromise(transaction.objectStore(storeName).getAll());
}

export async function getInterview(id) {
  const database = await openDatabase();
  const transaction = database.transaction("interviews", "readonly");
  return requestPromise(transaction.objectStore("interviews").get(id));
}

export async function saveInterview(interview) {
  const record = deepClone({ ...interview, updatedAt: nowIso() });
  const database = await openDatabase();
  const transaction = database.transaction("interviews", "readwrite");
  transaction.objectStore("interviews").put(record);
  await transactionPromise(transaction);
  return record;
}

export async function listInterviews() {
  const records = await getAll("interviews");
  return records.sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)));
}

export async function getLatestCompletedInterview() {
  const records = await listInterviews();
  return records.find(record => !["draft", "abandoned", "screened_out"].includes(record.status) && !record.duplicate_attempt && !record.deletion?.requested) || null;
}

function defaultSettings() {
  return {
    key: SETTINGS_KEY,
    interviewerCode: "PESQ-01",
    deviceId: `TAB-${shortId(createUuid())}`,
    autoSync: true,
    nextBlockIndex: 0,
    updatedAt: nowIso()
  };
}

function sanitizeSettings(record = {}) {
  const defaults = defaultSettings();
  return {
    key: SETTINGS_KEY,
    interviewerCode: String(record.interviewerCode || defaults.interviewerCode),
    deviceId: String(record.deviceId || defaults.deviceId),
    autoSync: record.autoSync !== false,
    nextBlockIndex: Number.isFinite(Number(record.nextBlockIndex)) ? Number(record.nextBlockIndex) : 0,
    updatedAt: record.updatedAt || defaults.updatedAt
  };
}

export async function getSettings() {
  const database = await openDatabase();
  const transaction = database.transaction("settings", "readonly");
  const stored = await requestPromise(transaction.objectStore("settings").get(SETTINGS_KEY));
  if (stored) {
    const clean = sanitizeSettings(stored);
    if ("endpointUrl" in stored || "apiKey" in stored || "adminApiKey" in stored) {
      const cleanupTransaction = database.transaction("settings", "readwrite");
      cleanupTransaction.objectStore("settings").put(clean);
      await transactionPromise(cleanupTransaction);
    }
    return clean;
  }

  const initial = defaultSettings();
  await saveSettings(initial);
  return initial;
}

export async function saveSettings(changes) {
  const database = await openDatabase();
  const readTransaction = database.transaction("settings", "readonly");
  const current = await requestPromise(readTransaction.objectStore("settings").get(SETTINGS_KEY));
  const next = sanitizeSettings({
    ...defaultSettings(),
    ...(current || {}),
    ...deepClone(changes),
    key: SETTINGS_KEY,
    updatedAt: nowIso()
  });
  const writeTransaction = database.transaction("settings", "readwrite");
  writeTransaction.objectStore("settings").put(next);
  await transactionPromise(writeTransaction);
  return next;
}

export async function claimNextBlock(numberOfBlocks) {
  const settings = await getSettings();
  const blockIndex = Number(settings.nextBlockIndex || 0) % numberOfBlocks;
  await saveSettings({ nextBlockIndex: (blockIndex + 1) % numberOfBlocks });
  return blockIndex;
}

export async function listQueueItems(options = {}) {
  const items = await getAll("syncQueue");
  const statuses = options.statuses || null;
  return items
    .filter(item => !statuses || statuses.includes(item.status))
    .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)));
}

async function putQueueItem(item) {
  const database = await openDatabase();
  const transaction = database.transaction("syncQueue", "readwrite");
  transaction.objectStore("syncQueue").put(deepClone(item));
  await transactionPromise(transaction);
  return item;
}

export async function enqueueInterview(interview) {
  const queue = await listQueueItems();
  const superseded = queue.filter(item =>
    item.interviewId === interview.id &&
    item.action === "upsert_interview" &&
    ["pending", "error"].includes(item.status)
  );

  for (const item of superseded) {
    await putQueueItem({ ...item, status: "superseded", supersededAt: nowIso() });
  }

  const queueItem = {
    id: `upsert:${interview.id}:r${interview.revision}`,
    requestId: createUuid(),
    interviewId: interview.id,
    revision: interview.revision,
    action: "upsert_interview",
    status: "pending",
    attempts: 0,
    createdAt: nowIso(),
    payload: deepClone(interview)
  };
  return putQueueItem(queueItem);
}

export async function enqueueDeletion(interview) {
  const version = Number(interview.deletion?.version || 1);
  const queueItem = {
    id: `delete:${interview.id}:v${version}`,
    requestId: createUuid(),
    interviewId: interview.id,
    revision: interview.revision,
    action: "request_deletion",
    status: "pending",
    attempts: 0,
    createdAt: nowIso(),
    payload: {
      interview_id: interview.id,
      revision: interview.revision,
      experiment_id: interview.experimentId,
      experiment_version: interview.experimentVersion,
      reason: interview.deletion.reason,
      requested_at: interview.deletion.requestedAt,
      interviewer_code: interview.interviewerCode,
      device_id: interview.deviceId
    }
  };
  return putQueueItem(queueItem);
}

export async function markQueueSending(id) {
  const items = await listQueueItems();
  const item = items.find(candidate => candidate.id === id);
  if (!item) throw new Error("Item de sincronização não encontrado.");
  return putQueueItem({
    ...item,
    status: "syncing",
    attempts: Number(item.attempts || 0) + 1,
    lastAttemptAt: nowIso(),
    lastError: null
  });
}

export async function markQueueSuccess(id, response) {
  const items = await listQueueItems();
  const item = items.find(candidate => candidate.id === id);
  if (!item) return;
  await putQueueItem({ ...item, status: "synced", syncedAt: nowIso(), response: deepClone(response) });

  const interview = await getInterview(item.interviewId);
  if (!interview) return;

  if (item.action === "request_deletion") {
    interview.deletion = {
      ...interview.deletion,
      syncStatus: "synced",
      syncedAt: nowIso()
    };
  } else if (Number(interview.revision) === Number(item.revision)) {
    interview.syncStatus = "synced";
    interview.lastSyncedAt = nowIso();
    interview.serverReceipt = deepClone(response);
  }
  await saveInterview(interview);
}

export async function markQueueError(id, error) {
  const items = await listQueueItems();
  const item = items.find(candidate => candidate.id === id);
  if (!item) return;
  await putQueueItem({
    ...item,
    status: "error",
    lastError: String(error?.message || error),
    lastAttemptAt: nowIso()
  });

  if (item.action === "upsert_interview") {
    const interview = await getInterview(item.interviewId);
    if (interview && Number(interview.revision) === Number(item.revision)) {
      interview.syncStatus = "error";
      await saveInterview(interview);
    }
  }
}

export async function recoverInterruptedSync() {
  const syncingItems = await listQueueItems({ statuses: ["syncing"] });
  for (const item of syncingItems) {
    await putQueueItem({ ...item, status: "pending", lastError: "Envio interrompido antes da confirmação." });
  }
}

export async function dashboardSummary() {
  const [interviews, activeQueue] = await Promise.all([
    listInterviews(),
    listQueueItems({ statuses: ["pending", "error", "syncing"] })
  ]);

  return {
    total: interviews.filter(item => Number(item.revision || 0) > 0 && item.status !== "abandoned" && isResearchRecord(item)).length,
    drafts: interviews.filter(item => item.status === "draft").length,
    pending: activeQueue.length,
    synced: interviews.filter(item => item.syncStatus === "synced").length,
    errors: activeQueue.filter(item => item.status === "error").length,
    deletionPending: interviews.filter(item => item.deletion?.requested && !item.deletion?.syncedAt).length,
    interviews,
    queue: activeQueue
  };
}

export async function buildLocalBackup(experiment) {
  const [interviews, queue, settings] = await Promise.all([
    listInterviews(),
    listQueueItems(),
    getSettings()
  ]);
  return {
    backup_schema_version: "1.0.0",
    exported_at: nowIso(),
    experiment: {
      id: experiment.experiment.id,
      version: experiment.experiment.version,
      app_version: experiment.experiment.app_version
    },
    device: {
      device_id: settings.deviceId,
      interviewer_code: settings.interviewerCode
    },
    interviews,
    sync_queue: queue
  };
}
